/**
 * PrintHQ Vault files in the web app (Files module).
 *
 * Vault (the desktop app) syncs models and their files to PrintHQ Cloud:
 * `vault_cloud_models` (name, folder path, tags, metadata incl. a small
 * thumbnail) and `vault_objects` (one row per file). RLS lets the owner read
 * both directly; downloads, deletes and share links go through the
 * `vault-api` edge function, which signs storage URLs (the `vault` bucket is
 * private with no storage policies).
 *
 * Renaming and moving happen in the Vault app so files on the user's
 * computers never change unexpectedly.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { demoVaultModels, demoVaultObjects } from '@/utils/demoVault'

export interface VaultModel {
  id: string
  name: string
  folder_path: string | null
  tags: string[]
  primary_object_id: string | null
  preview_object_id: string | null
  description: string | null
  source_url: string | null
  creator: string | null
  license: string | null
  created_at: string
  updated_at: string
}

export interface VaultObject {
  id: string
  model_id: string | null
  file_name: string
  file_ext: string | null
  size_bytes: number
  current_version: number
  created_at: string
  updated_at: string
}

/** Parsed facts the desktop app stores with a model (subset used here). */
export interface VaultModelMeta {
  thumb?: string
  dims?: [number, number, number]
  color_count?: number
  is_multicolor?: boolean
  printer_model?: string | null
  slicer?: string | null
}

export interface VaultShareResult {
  url: string
  expires_at: string | null
}

const PAGE = 1000

async function api<T>(path: string, body?: unknown, method = 'POST'): Promise<T> {
  const { supabase } = await import('@/services/supabase')
  const { data, error } = await supabase.functions.invoke(`vault-api${path}`, {
    method: method as 'POST' | 'GET',
    body: body as Record<string, unknown> | undefined,
  })
  if (error) {
    // FunctionsHttpError carries the response; surface the server's message.
    const ctx = (error as { context?: Response }).context
    let message = error.message
    try {
      const j = ctx ? await ctx.json() : null
      if (j?.message) message = j.message
    } catch {
      /* keep generic message */
    }
    throw new Error(message)
  }
  return data as T
}

export const useVaultStore = defineStore('vault', () => {
  const models = ref<VaultModel[]>([])
  const objects = ref<VaultObject[]>([])
  const meta = ref<Record<string, VaultModelMeta>>({})
  const loading = ref(false)
  const loaded = ref(false)
  const error = ref<string | null>(null)
  let metaInFlight = new Set<string>()

  const modelById = computed(() => new Map(models.value.map((m) => [m.id, m])))
  const totalBytes = computed(() => objects.value.reduce((s, o) => s + (o.size_bytes || 0), 0))

  /** Load every synced model and file (paged: PostgREST caps rows per request). */
  async function fetchAll(force = false) {
    if (loading.value || (loaded.value && !force)) return
    const auth = useAuthStore()
    loading.value = true
    error.value = null
    try {
      if (auth.isDemoMode) {
        models.value = demoVaultModels
        objects.value = demoVaultObjects
        loaded.value = true
        return
      }
      if (!auth.userId) return
      const { supabase } = await import('@/services/supabase')
      const pageAll = async <T>(table: string, cols: string, extra: (q: any) => any): Promise<T[]> => {
        const out: T[] = []
        for (let from = 0; ; from += PAGE) {
          const { data, error: e } = await extra((supabase as any).from(table).select(cols))
            .order('id')
            .range(from, from + PAGE - 1)
          if (e) throw e
          out.push(...((data ?? []) as T[]))
          if (!data || data.length < PAGE) break
        }
        return out
      }
      const [m, o] = await Promise.all([
        pageAll<VaultModel>(
          'vault_cloud_models',
          'id,name,folder_path,tags,primary_object_id,preview_object_id,description,source_url,creator,license,created_at,updated_at',
          (q) => q.is('deleted_at', null),
        ),
        pageAll<VaultObject>(
          'vault_objects',
          'id,model_id,file_name,file_ext,size_bytes,current_version,created_at,updated_at',
          (q) => q.is('deleted_at', null).gt('current_version', 0),
        ),
      ])
      models.value = m
      objects.value = o
      loaded.value = true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Could not load Vault files'
      console.error('Vault load failed:', e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Model metadata (thumbnail, dimensions, colors) for the given models,
   * fetched in batches only when cards become visible: a thumbnail is up to
   * ~60 KB, too much to load for a whole library up front.
   */
  async function loadMeta(modelIds: string[]) {
    const auth = useAuthStore()
    const want = modelIds.filter((id) => !(id in meta.value) && !metaInFlight.has(id))
    if (!want.length) return
    want.forEach((id) => metaInFlight.add(id))
    try {
      if (auth.isDemoMode) {
        const { demoVaultMeta } = await import('@/utils/demoVault')
        for (const id of want) meta.value[id] = demoVaultMeta[id] ?? {}
        return
      }
      const { supabase } = await import('@/services/supabase')
      for (let i = 0; i < want.length; i += 40) {
        const batch = want.slice(i, i + 40)
        // Vault tables aren't in the generated DB types; query them untyped.
        const { data, error: e } = await (supabase as any).from('vault_cloud_models').select('id,metadata').in('id', batch)
        if (e) throw e
        for (const row of (data ?? []) as { id: string; metadata: VaultModelMeta | null }[]) meta.value[row.id] = row.metadata ?? {}
        for (const id of batch) if (!(id in meta.value)) meta.value[id] = {}
      }
    } catch (e) {
      console.error('Vault thumbnails failed:', e)
    } finally {
      want.forEach((id) => metaInFlight.delete(id))
    }
  }

  /** A short-lived signed download URL for a file's current version. */
  async function downloadUrl(objectId: string): Promise<string> {
    const auth = useAuthStore()
    if (auth.isDemoMode) throw new Error('Downloads are not available in demo mode')
    const r = await api<{ url: string }>('/objects/download-url', { object_id: objectId })
    return r.url
  }

  /**
   * Remove a file from PrintHQ Cloud. Copies on the user's computers stay;
   * Vault stops syncing them.
   */
  async function deleteObject(objectId: string) {
    const auth = useAuthStore()
    if (!auth.isDemoMode) await api('/objects/delete', { object_id: objectId })
    objects.value = objects.value.filter((o) => o.id !== objectId)
  }

  /** One share link for one or more Vault files. */
  async function createShare(
    objectIds: string[],
    opts: { title?: string | null; expiresInHours: number | null; password?: string | null; maxDownloads?: number | null },
  ): Promise<VaultShareResult> {
    const auth = useAuthStore()
    if (auth.isDemoMode) throw new Error('Share links are not available in demo mode')
    const body: Record<string, unknown> = {
      expires_in_hours: opts.expiresInHours,
      password: opts.password || null,
      allow_download: true,
      max_downloads: opts.maxDownloads ?? null,
    }
    if (objectIds.length === 1 && !opts.title) body.object_id = objectIds[0]
    else {
      body.items = objectIds.map((object_id) => ({ object_id }))
      body.title = opts.title || null
    }
    const r = await api<{ token: string; url?: string; share: { expires_at: string | null } }>('/shares', body)
    const base = (import.meta.env.VITE_APP_URL as string | undefined) || window.location.origin
    return { url: r.url || `${base.replace(/\/+$/, '')}/vault/s/${r.token}`, expires_at: r.share?.expires_at ?? null }
  }

  function clear() {
    models.value = []
    objects.value = []
    meta.value = {}
    loaded.value = false
    metaInFlight = new Set()
  }

  return { models, objects, meta, loading, loaded, error, modelById, totalBytes, fetchAll, loadMeta, downloadUrl, deleteObject, createShare, clear }
})
